import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { TodoTemplateModalForm } from "~/components/common/todo_templates/TodoTemplateModalForm";
import type { DealType } from "~/models/Deal";
import type { TodoTemplate, TodoTemplateEdit } from "~/models/TodoTemplate";
import {
	buildInitialTodoTemplateEdit,
	validationSchema
} from "~/models/TodoTemplate";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/TodoTemplate";
import { onInvalidScroll } from "~/utils/FormUtil";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	dealType: DealType;
	handleClose: () => void;
	subtypeId: number | null;
	todoTemplate?: TodoTemplate | null;
}>;

export const TodoTemplateModal: React.FC<Props> = ({
	dealType,
	subtypeId,
	handleClose,
	todoTemplate = null
}: Props) => {
	const { mutate: globalMutate } = useSWRConfig();
	const methods = useForm<TodoTemplateEdit>({
		defaultValues:
			todoTemplate ?? buildInitialTodoTemplateEdit(dealType, subtypeId),
		mode: "onBlur",
		resolver: yupResolver(validationSchema)
	});
	const handleModalSubmit = React.useCallback(() => {
		methods.handleSubmit(
			async (submitValue: TodoTemplateEdit) => {
				if (todoTemplate === null) {
					// create
					const response = await create(submitValue);
					if (isSuccess(response)) {
						await globalMutate(generatePath("/todo_templates"));
						handleClose();
						toast.success("作成しました");
						methods.reset();
						return;
					}
					toast.error("エラーが発生しました");
					return;
				}
				// update
				const response = await update(todoTemplate.id, submitValue);
				if (isSuccess(response)) {
					await globalMutate(generatePath("/todo_templates"));
					handleClose();
					toast.success("更新しました");
					return;
				}
				toast.error("エラーが発生しました");
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [methods, todoTemplate, globalMutate, handleClose]);
	return (
		<Modalv2 isOpen onClose={handleClose} size="l">
			<ModalHeader
				title={
					todoTemplate ? "TODOテンプレート編集" : "TODOテンプレート新規作成"
				}
			/>
			<ModalBody>
				<FormProvider {...methods}>
					<TodoTemplateModalForm />
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						onClick={(): void => {
							methods.reset();
							handleClose();
						}}
						variant="secondary"
					>
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button
						disabled={methods.formState.isSubmitting}
						onClick={handleModalSubmit}
						variant="primary"
					>
						保存
					</Button>
				}
			/>
		</Modalv2>
	);
};
